import { FC, useEffect, useState } from 'react'
import { FormHelperText, Typography } from '@material-ui/core'
import { addBusinessDays } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { DatePicker } from '../../..'
import Checkbox from '../../molecules/Checkbox'
import useStyles from './styles'

export type PreferredTimeWindow = 'morning' | 'afternoon'

/**
 * Time windows in local time.
 * These are converted to UTC based on the property's timezone.
 * If no timezone is provided, defaults to the user's browser timezone.
 */
const WINDOW_HOURS_LOCAL: Record<PreferredTimeWindow, number> = {
  morning: 8,   // 8am - 12pm local time
  afternoon: 12, // 12pm - 5pm local time
}

/**
 * Get the user's browser timezone as fallback.
 */
const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'America/New_York' // Final fallback if browser API fails
  }
}

interface PreferredSchedulingProps {
  value?: number | null
  onChange: (timestamp: number) => void
  showWarnings?: boolean
  /** Property timezone (e.g., 'America/New_York', 'America/Los_Angeles'). Defaults to user's browser timezone. */
  timezone?: string
}

/**
 * Convert UTC timestamp to local time window.
 * Uses Intl.DateTimeFormat for timezone conversion.
 */
const getWindowFromTimestamp = (
  value?: number | null,
  timezone?: string
): PreferredTimeWindow | undefined => {
  if (!value) return undefined

  // Convert to local time in the property's timezone
  const date = new Date(value * 1000)
  const tz = timezone || getUserTimezone()
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hour12: false,
    timeZone: tz,
  })
  const localHour = parseInt(formatter.format(date), 10)

  if (localHour === WINDOW_HOURS_LOCAL.morning) return 'morning'
  if (localHour === WINDOW_HOURS_LOCAL.afternoon) return 'afternoon'
  return undefined
}

const PreferredScheduling: FC<PreferredSchedulingProps> = ({
  value,
  onChange,
  showWarnings,
  timezone,
}) => {
  const classes = useStyles()

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? new Date(value * 1000) : null
  )
  const [timeWindow, setTimeWindow] = useState<PreferredTimeWindow | undefined>(
    getWindowFromTimestamp(value, timezone)
  )

  useEffect(() => {
    setSelectedDate(value ? new Date(value * 1000) : null)
    setTimeWindow(getWindowFromTimestamp(value, timezone))
  }, [value, timezone])

  /**
   * Convert local time selection to UTC timestamp.
   * Creates a timestamp representing the selected date and time window
   * in the property's local timezone.
   */
  const emitChange = (
    date: Date | null,
    window: PreferredTimeWindow | undefined
  ) => {
    if (!date || !window) return
    
    const tz = timezone || getUserTimezone()
    const localHour = WINDOW_HOURS_LOCAL[window]
    
    // Create date string in YYYY-MM-DD format
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const dateStr = `${year}-${month}-${day}T${String(localHour).padStart(2, '0')}:00:00`

    const utcDate = zonedTimeToUtc(dateStr, tz)
    const utcTimestamp = Math.floor(utcDate.getTime() / 1000)
    
    onChange(utcTimestamp)
  }

  const handleDateChange = (date: Date) => {
    setSelectedDate(date)
    emitChange(date, timeWindow)
  }

  const handleWindowChange = (window: PreferredTimeWindow) => {
    const newWindow = timeWindow === window ? undefined : window
    setTimeWindow(newWindow)
    emitChange(selectedDate, newWindow)
  }

  const hasError = !!showWarnings && (!selectedDate || !timeWindow)

  return (
    <div className={classes.root}>
      <Typography variant="body1" className={classes.label}>
        <span className={hasError ? classes.requiredError : classes.required}>*</span>{' '}
        Preferred Scheduling:
      </Typography>
      <div className={classes.datePickerWrapper}>
        <DatePicker
          minDate={addBusinessDays(new Date(), 1)}
          value={selectedDate}
          onChange={handleDateChange}
          error={hasError && !selectedDate}
          disableWeekends
          disableSameDay
          disableHolidays
        />
      </div>
      <div className={classes.checkboxesContainer}>
        <Checkbox
          text="Morning (8am - 12pm)"
          checked={timeWindow === 'morning'}
          onChange={() => handleWindowChange('morning')}
          error={hasError && !timeWindow}
        />
        <Checkbox
          text="Afternoon (12pm - 5pm)"
          checked={timeWindow === 'afternoon'}
          onChange={() => handleWindowChange('afternoon')}
          error={hasError && !timeWindow}
        />
      </div>
      {hasError && (
        <FormHelperText error>
          Please select a date and a preferred time window.
        </FormHelperText>
      )}
    </div>
  )
}

export default PreferredScheduling
